import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import CountrySelector, { getDefaultCountry } from '../components/auth/CountrySelector';
import { otpService, isNigerianNumber } from '../services/otpService';
import { initializeFirebase } from '../services/firebase';
import RecaptchaBottomSheet from '../components/ui/RecaptchaBottomSheet';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { setUserProfile } = useAuth();
  const { isDesktop } = useResponsive();

  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(getDefaultCountry);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Bottom sheet state
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState('');

  // Get full phone number with country code
  const getFullPhoneNumber = () => {
    let phoneValue = phone;
    if (!phone.startsWith('+')) {
      phoneValue = selectedCountry.dialCode + phone.replace(/^0+/, '');
    }
    return phoneValue;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const phoneValue = getFullPhoneNumber();

    // For Nigerian numbers, use Termii (no reCAPTCHA needed)
    if (isNigerianNumber(phoneValue)) {
      await sendOtpAndNavigate(phoneValue);
    } else {
      // For non-Nigerian numbers, show reCAPTCHA bottom sheet
      setPendingPhoneNumber(phoneValue);
      setShowRecaptcha(true);
      // Initialize Firebase while sheet is opening
      await initializeFirebase();
    }
  };

  // Called when reCAPTCHA is ready in bottom sheet
  const handleRecaptchaVerified = async () => {
    if (pendingPhoneNumber) {
      await sendOtpAndNavigate(pendingPhoneNumber);
    }
  };

  const sendOtpAndNavigate = async (phoneValue) => {
    setIsLoading(true);
    setShowRecaptcha(false);

    try {
      // Send OTP via appropriate provider
      await otpService.sendOtp(phoneValue, 'recaptcha-sheet-container');

      // Store user profile and route to OTP verification
      setUserProfile((prev) => ({
        ...prev,
        firstName,
        lastName,
        country: selectedCountry.code,
        phone: phoneValue,
        referralCode: referralCode || undefined,
      }));
      navigate('/verify-otp');
    } catch (err) {
      console.error('Failed to send OTP:', err);
      setError(err.message || 'Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecaptchaError = (err) => {
    console.error('reCAPTCHA error:', err);
    setShowRecaptcha(false);
    setError('Verification failed. Please try again.');
  };

  const isValid = phone.length > 0 && firstName.length > 0 && lastName.length > 0;

  return (
    <div className="min-h-screen min-h-dvh flex flex-col bg-white">
      <div className={`flex-1 flex flex-col ${isDesktop ? 'max-w-md mx-auto w-full' : ''}`}>
        {/* Header */}
        <header className="sticky top-0 bg-white z-10 flex items-center gap-4 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] border-b border-gray-100">
          <button
            onClick={() => navigate('/welcome')}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">Create Account</h1>
        </header>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 px-6 py-8 flex flex-col">
          <p className="text-gray-600 mb-6">
            Provide the details below to create your account.
          </p>

          <div className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  className="input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="flex gap-2">
                <CountrySelector
                  selectedCountry={selectedCountry}
                  onSelectCountry={setSelectedCountry}
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="801 234 5678"
                  className="input flex-1"
                />
              </div>
            </div>

            {/* Referral Code (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Referral Code <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Gift size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="Enter referral code"
                  className="input pl-11"
                />
              </div>
              {referralCode && (
                <p className="text-xs text-emerald-600 mt-1">You'll get $10 when you make your first purchase!</p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>

          <div className="flex-1" />

          <button
            type="submit"
            disabled={!isValid || isLoading}
            className={`w-full py-4 rounded-2xl font-bold text-lg transition-colors mt-4 ${
              isValid && !isLoading
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sending code...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </form>
      </div>

      {/* reCAPTCHA Bottom Sheet */}
      <RecaptchaBottomSheet
        isOpen={showRecaptcha}
        onClose={() => setShowRecaptcha(false)}
        onVerified={handleRecaptchaVerified}
        onError={handleRecaptchaError}
      />
    </div>
  );
}
